import { Injectable, Inject, Optional, InjectionToken } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Observable, from, throwError, BehaviorSubject } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { Router } from "@angular/router";
import { Storage } from "@ionic/storage";
import { AuthService } from "../auth.service";
import { EventsService } from "../events.service";
import { environment } from "src/environments/environment";

export type INTERCEPTOR_CONFIG = { [x: string]: INTERCEPTOR_CONFIG_URLS };
export type INTERCEPTOR_CONFIG_URLS = {
  name: string;
  url: string;
  headers?: any;
};

export const INTERCEPTOR_CONFIG_TOKEN = new InjectionToken<any>(
  "Interceptor Configuration",
  { factory: () => {} }
);

@Injectable({
  providedIn: "root",
})
export class NetworkInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  private tokenSkipUrls: string[] = [
    // "auth/rest/login/validateToken", // never attach Bearer here
  ];

  constructor(
    @Inject(INTERCEPTOR_CONFIG_TOKEN) @Optional() public readonly config: any,
    private router: Router,
    private storage: Storage,
    private auth: AuthService,
    private eventSevice: EventsService
  ) {
    config && (this.config = { ...(config[0] || config) });
  }

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.storage.get("JWTToken")).pipe(
      switchMap((token) => {
        // Always add API Gateway header
        const gatewayHeaders: any = {};
        gatewayHeaders[environment["API_GATEWAY_KEY"]] =
          environment["API_GATEWAY_ID"];
        request = request.clone({ setHeaders: gatewayHeaders });

        // Attach Authorization only if not skip URL old
        // if (token && this.shouldSendToken(request.url)) {
        //   request = request.clone({
        //     setHeaders: { Authorization: `Bearer ${token}` },
        //   });
        // }

        if (token && this.shouldSendToken(request.url)) {
          if (request.url.includes("validateToken")) {
            request = request.clone({
              setHeaders: { Authorization: `${token}` },
            });
          } else {
            request = request.clone({
              setHeaders: { Authorization: `${token}` },
            });
          }
        }

        return next.handle(request).pipe(
          catchError((err) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              console.warn("[Interceptor] 401 → refresh attempt...");
              return this.handle401Error(request, next);
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  private shouldSendToken(url: string): boolean {
    return !this.tokenSkipUrls.some((skipUrl) => new RegExp(skipUrl).test(url));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {

    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((t) => t != null),
        take(1),
        switchMap((token) => {
          const retried = request.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
          });
          return next.handle(retried);
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.auth.refreshToken().pipe(
      switchMap((newToken: string) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);

        const retried = request.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` },
        });
        return next.handle(retried);
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.logout();
        return throwError(() => err);
      })
    );
  }

  private async logout() {
    await this.auth.clearSession();
    await this.eventSevice.emitUserLogout(true);
    await this.router.navigate(["/login"], { replaceUrl: true });
  }
}
